import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { ChevronLeft, Check, AlertCircle, } from "lucide-react";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { getUserProfile } from "../services/api";
import {
  Button,
  useMediaQuery,
} from "@mui/material";



export function PetFormHeader({ currentPageId, isEditMode, petId, petData }) {
  const navigate = useNavigate();

  // 滾動陰影控制引用
  const tabScrollRef = useRef(null);
  const leftShadowRef = useRef(null);
  const rightShadowRef = useRef(null);
  const activeTabRefs = useRef({});
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const userRole = Cookies.get("userRole") || '';
  const isMobile = useMediaQuery("(max-width:900px)");

  // Track visited pages using cookies (7-day expiration)
  const storageKey = isEditMode && petId ? `visitedPages_${petId}` : 'visitedPages_new';

  const [visitedPages, setVisitedPages] = useState(() => {
    // Restore from cookies for both new and edit mode
    try {
      const stored = Cookies.get(storageKey);
      const pages = stored ? new Set(JSON.parse(stored)) : new Set();
      pages.add(currentPageId);
      return pages;
    } catch {
      return new Set([currentPageId]);
    }
  });

  // Update visited pages when currentPageId changes
  useEffect(() => {
    setVisitedPages(prev => {
      const updated = new Set([...prev, currentPageId]);

      // Save to cookies with 7-day expiration
      try {
        Cookies.set(storageKey, JSON.stringify([...updated]), { expires: 1 });
      } catch (error) {
        console.error('Failed to save visited pages:', error);
      }

      return updated;
    });
  }, [currentPageId, storageKey]);

  // Function to check if basic page fields are filled
  const isBasicComplete = () => {
    if (!petData) return false;
    return !!(
      petData.name &&
      petData.birthday &&
      petData.sex &&
      petData.sterilization !== undefined &&
      petData.animal &&
      petData.owner &&
      petData.ownerContact1
    );
  };

  // Function to check if details page fields are filled
  const isDetailsComplete = () => {
    if (!petData) return false;
    return (
      petData.chipId ||
      petData.placeOfBirth ||
      petData.motherName ||
      petData.motherChip ||
      petData.motherBreed ||
      petData.motherPlaceOfBirth ||
      petData.motherDOB ||
      petData.motherParity ||
      petData.fatherName ||
      petData.fatherChip ||
      petData.fatherBreed ||
      petData.fatherPlaceOfBirth ||
      petData.fatherDOB ||
      petData.transfer.length > 0
    );
  };

  // Function to check if medical page has data
  const isMedicalComplete = () => {
    if (!petData) return false;
    return !!(petData.medical && petData.medical.length > 0);
  };

  // Function to check if medication page has data
  const isMedicationComplete = () => {
    if (!petData) return false;
    return !!(petData.medication && petData.medication.length > 0);
  };

  // Function to check if deworm page has data
  const isDewormComplete = () => {
    if (!petData) return false;
    return !!(petData.deworm && petData.deworm.length > 0);
  };

  // Function to check if vaccination page has data
  const isVaccinationComplete = () => {
    if (!petData) return false;
    return !!(petData.vaccineRecords && petData.vaccineRecords.length > 0);
  };

  const isFaceIDComplete = () => {
    if (!petData) return false;
    return petData.isRegistered;
  };

  const initialPages = [{
    id: "basic",
    name: t("Basic"),
    status: isBasicComplete() ? "completed" : "not-started",
    route: "/pet-management/add",
  },
  {
    id: "details",
    name: t("Details"),
    status: isDetailsComplete() ? "completed" : "not-started",
    route: "/pet-management/add/details",
  },
  {
    id: "medical",
    name: t("Medical"),
    status: isMedicalComplete() ? "completed" : "not-started",
    route: "/pet-management/add/medical",
  },
  {
    id: "medication",
    name: t("Medication"),
    status: isMedicationComplete() ? "completed" : "not-started",
    route: "/pet-management/add/medication",
  },
  {
    id: "deworm",
    name: t("Deworming"),
    status: isDewormComplete() ? "completed" : "not-started",
    route: "/pet-management/add/deworming",
  },
  {
    id: "vaccination",
    name: t("Vaccine"),
    status: isVaccinationComplete() ? "completed" : "not-started",
    route: "/pet-management/add/vaccination",
  },
  {
    id: "faceID",
    name: t("FaceId"),
    status: isFaceIDComplete() ? "completed" : "not-started",
    route: "/pet-management/add/faceID",
  }
  ];

  const updateScrollShadows = () => {
    if (
      !tabScrollRef.current ||
      !leftShadowRef.current ||
      !rightShadowRef.current
    )
      return;

    const { scrollLeft, scrollWidth, clientWidth } = tabScrollRef.current;

    // 左側陰影 - 當滾動位置 > 0 時顯示
    if (scrollLeft > 0) {
      leftShadowRef.current.classList.remove("opacity-0");
    } else {
      leftShadowRef.current.classList.add("opacity-0");
    }

    // 右側陰影 - 當還有內容可滾動時顯示
    if (Math.ceil(scrollLeft + clientWidth) < scrollWidth) {
      rightShadowRef.current.classList.remove("opacity-0");
    } else {
      rightShadowRef.current.classList.add("opacity-0");
    }
  };

  // 監聽滾動事件
  useEffect(() => {
    const scrollContainer = tabScrollRef.current;
    if (scrollContainer) {
      const handleScroll = () => {
        updateScrollShadows();
        // Save scroll position to cookies with 1-day expiration
        try {
          Cookies.set(`${storageKey}_scroll`, scrollContainer.scrollLeft.toString(), { expires: 1 });
        } catch (error) {
          console.error('Failed to save scroll position:', error);
        }
      };

      scrollContainer.addEventListener("scroll", handleScroll);

      // Restore scroll position from cookies on mount
      try {
        const savedScroll = Cookies.get(`${storageKey}_scroll`);
        if (savedScroll) {
          scrollContainer.scrollLeft = parseInt(savedScroll, 10);
        }
      } catch (error) {
        console.error('Failed to restore scroll position:', error);
      }

      // 初始化時運行一次
      updateScrollShadows();

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
      };
    }
  }, [storageKey, isEditMode]);

  // 當窗口大小改變時重新檢查
  useEffect(() => {
    window.addEventListener("resize", updateScrollShadows);
    return () => {
      window.removeEventListener("resize", updateScrollShadows);
    };
  }, []);

  // 獲取頁面狀態提示文字
  const getStatusTooltip = (status) => {
    switch (status) {
      case "completed":
        return "已完成";
      case "error":
        return "有錯誤需要修正";
      case "in-progress":
        return "正在填寫中";
      default:
        return "尚未開始填寫";
    }
  };

  // 頁面導航處理函數
  const handlePageNavigation = (route) => {
    const targetRoute =
      isEditMode && petId ? route.replace("/add", `/edit/${petId}`) : route;
    navigate(targetRoute, { state: { petData } });
  };

  // 獲取頁面標題
  const getPageTitle = () => {
    return isEditMode ? t("EditPetProfile") : t("AddPets");
  };

  return (
    <div className="relative mb-4 md:mb-8 max-w-[1440px]  sm:px-6">
      {/* 標題區與分頁標籤導航的容器 */}
      <div className="flex flex-col border-b border-[#D1D1D1]">
        {/* 標題區 */}
        <div className="flex flex-col md:flex-row items-start gap-3 md:gap-5 py-3 md:py-[15px] md:mr-10">
          <div className="flex flex-row gap-3">
            <Link
              to="/pet-management"
              className="flex items-center gap-2 md:gap-5 cursor-pointer"
            >
              <ChevronLeft />
              <span className="text-[20px] md:text-[34px] font-bold text-[#050505]">
                {getPageTitle()}
              </span>
            </Link>
            {(userRole === "admin" && isEditMode && isMobile) && (
            <Button
              variant="outlined"
              sx={{
                color: "#333333",
                borderColor: "#333333",
                background: currentPageId === "transfer" ? "#d8d8d8" : "transparent",
                minWidth: 80,
                fontWeight: 700,
                my: "auto",
                "&:hover": {
                  background: "#969696",
                },
              }}
              onClick={async () => {
                navigate(`/pet-management/edit/${petId}/pet_transfer`, { state: { petData } });
              }}
            >
              {t("Transfer")}
            </Button>)}
          </div>

          <div className="ml-4 px-4 py-2 bg-[#DEEDFF] text-[#65A8FB] text-sm my-auto border-l-2 border-[#65A8FB] font-bold">
            {t("AddInfoAnytime")}
          </div>
          {(userRole === "admin" && isEditMode && !isMobile) && (
            <Button
              variant="outlined"
              sx={{
                color: "#333333",
                borderColor: "#333333",
                background: currentPageId === "transfer" ? "#d8d8d8" : "transparent",
                minWidth: 80,
                fontWeight: 700,
                my: "auto",
                "&:hover": {
                  background: "#969696",
                },
              }}
              onClick={async () => {
                navigate(`/pet-management/edit/${petId}/pet_transfer`, { state: { petData } });
              }}
            >
              {t("Transfer")}
            </Button>)}
        </div>

        {isEditMode && (
          <>
            {/* 分頁標籤導航 - 桌面版 */}
            <div className="hidden md:flex overflow-x-auto justify-between">
              {initialPages.map((page) => {
                return (
                  <div key={page.id} className="relative group">
                    <div
                      className={`flex items-center px-[40px] py-[25px] gap-2 ${page.id === currentPageId
                        ? "border-b-2 border-black"
                        : "cursor-pointer"
                        }`}
                      onClick={() => handlePageNavigation(page.route)}
                    >
                      <div className={`flex items-center justify-center w-5 h-5 rounded-full ${page.status === 'completed' ? 'bg-[#FFB60C]' :
                        page.id === currentPageId ? 'bg-[#333333]' :
                          visitedPages.has(page.id) ? 'bg-[#969696]' : 'bg-[#E6E6E6]'
                        }`}>
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      </div>
                      <span className="text-[16px] font-bold text-[#282828] whitespace-nowrap">
                        {page.name}
                      </span>
                    </div>
                    <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                      {getStatusTooltip(page.status)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 分頁標籤導航 - 移動版 */}
            <div
              className="flex md:hidden overflow-x-auto pb-2 w-full scrollbar-hide relative"
              ref={tabScrollRef}
            >
              {/* 視覺提示（左側陰影，當可以向左滾動時顯示） */}
              <div
                ref={leftShadowRef}
                className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-[#FCF8F3] to-transparent z-10 pointer-events-none scroll-shadow-left opacity-0 transition-opacity duration-300"
              ></div>

              {/* 標籤容器 */}
              <div className="flex w-full px-1">
                {initialPages.map((page) => {
                  return (
                    <div
                      key={page.id}
                      className="relative group shrink-0"
                      ref={el => activeTabRefs.current[page.id] = el}
                    >
                      <div
                        className={`flex items-center px-3 md:px-4 py-2 md:py-3 mr-2 gap-1.5 ${page.id === currentPageId
                          ? "border-b-2 border-black bg-white/40 rounded-t-md"
                          : ""
                          }`}
                        onClick={() => handlePageNavigation(page.route)}
                      >
                        <div className={`flex items-center justify-center w-4 h-4 rounded-full ${page.status === 'completed' ? 'bg-[#FFB60C]' :
                          page.id === currentPageId ? 'bg-[#333333]' :
                            visitedPages.has(page.id) ? 'bg-[#969696]' : 'bg-[#E6E6E6]'
                          }`}>
                          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                        </div>
                        <span className="text-[13px] md:text-[14px] font-bold text-[#282828] whitespace-nowrap">
                          {page.name}
                        </span>
                      </div>
                      <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                        {getStatusTooltip(page.status)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 視覺提示（右側陰影，當可以向右滾動時顯示） */}
              {/* <div
                ref={rightShadowRef}
                className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-[#FCF8F3] to-transparent z-10 pointer-events-none scroll-shadow-right transition-opacity duration-300"
              ></div> */}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
